import "./styles.scss";

import type { ModalPropType } from "components/Management";
import { ManagementTab } from "components/Management";
import { Pending } from "components/Management/ModalContent";
import ModalBox from "components/Modal";
import type { TTags } from "components/Title";
import { withManagementEmptyTagFilterOption } from "constants/managementTagFilter";
import type { TPaidStatus } from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchBrands, IBrand } from "store/brandStore";
import type { TermState } from "store/managementStore";
import { useAppDispatch, useAppSelector } from "store/store";

import BrandAutocomplete from "../BrandAutocomplete";
import ChipsAutocomplete from "../ChipsAutocomplete";
import ViewColumnOptions from "../LastElementTable/ViewColumnOptions";
import SmallSearchBar from "../SmallSearchBar";
import PaymentFilter from "./PaymentFilter";

type TProps = {
    handleFilter: (e: React.ChangeEvent<HTMLInputElement>) => void;
    titleTags: TTags[];
    chosenTitleTags: string[];
    setChosenTitleTags: React.Dispatch<React.SetStateAction<string[]>>;
    beneficiaryTags: TTags[];
    chosenBeneficiaryTags: string[];
    setChosenBeneficiaryTags: React.Dispatch<React.SetStateAction<string[]>>;
    filter: string;
    tabValue: number;
    exportFile: () => void;
    exportBeneficiariesFile: () => void;
    exportStockFile: () => void;
    allChosenTags: any;
    setModalProp: (value: ModalPropType) => void;
    setChosenBrand: React.Dispatch<React.SetStateAction<string>>;
    setPaidStatus: React.Dispatch<React.SetStateAction<TPaidStatus>>;
    paidStatus: TPaidStatus;
    /**
     * After choosing a payment filter, reset table pagination
     */
    resetPagination: () => void;
};

type ManagementGeneralFilterProps = Partial<TermState> & TProps;

function ManagementGeneralFilter({
    handleFilter,
    titleTags,
    chosenTitleTags,
    setChosenTitleTags,
    beneficiaryTags,
    chosenBeneficiaryTags,
    setChosenBeneficiaryTags,
    exportFileStatus,
    filter,
    tabValue,
    setChosenBrand,
    setPaidStatus,
    paidStatus,
    resetPagination,
}: ManagementGeneralFilterProps) {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { getCompanySettings, companyInfos } = useCompanySettings();
    const usesMultipleBrand = getCompanySettings("uses_multiple_brands");
    const { brands } = useAppSelector((state) => state.brands);

    useEffect(() => {
        if (usesMultipleBrand && !brands.length) dispatch(fetchBrands(true));
    }, [usesMultipleBrand, brands]);

    const titleTagOptions = useMemo(
        () => withManagementEmptyTagFilterOption(titleTags ?? [], t("Filters.EmptyTag")),
        [titleTags, t],
    );
    const beneficiaryTagOptions = useMemo(
        () => withManagementEmptyTagFilterOption(beneficiaryTags ?? [], t("Filters.EmptyTag")),
        [beneficiaryTags, t],
    );

    return (
        <>
            <ModalBox open={exportFileStatus === "pending"} onClose={() => {}} showCloseButton={false}>
                <Pending phrase={t("Management.ExportingFile")} />
            </ModalBox>
            <div className="management-table-search">
                <SmallSearchBar filter={filter} onSearch={handleFilter} />
                {/* Tags filter */}
                <div className="management-filters-select">
                    {usesMultipleBrand && tabValue === ManagementTab.GENERAL && (
                        <BrandAutocomplete
                            brands={brands}
                            setChosenBrand={setChosenBrand}
                            companyName={companyInfos?.name}
                        />
                    )}
                    {tabValue === ManagementTab.GENERAL && (
                        <ChipsAutocomplete
                            options={titleTagOptions}
                            label={t("Stock.Titles")}
                            noOptionsText={t("Filters.EmptyStateTitleTags")}
                            filter={filter}
                            chosenTags={chosenTitleTags}
                            setChosenTags={setChosenTitleTags}
                        />
                    )}
                    {tabValue === ManagementTab.BENEFICIARIES && (
                        <PaymentFilter
                            paidStatus={paidStatus}
                            setPaidStatus={setPaidStatus}
                            resetPagination={resetPagination}
                        />
                    )}
                    {(tabValue === ManagementTab.GENERAL || tabValue === ManagementTab.BENEFICIARIES) && (
                        <ChipsAutocomplete
                            options={beneficiaryTagOptions}
                            label={t("Filters.Beneficiaries")}
                            noOptionsText={t("Filters.EmptyStateBeneficiaryTags")}
                            filter={filter}
                            chosenTags={chosenBeneficiaryTags}
                            setChosenTags={setChosenBeneficiaryTags}
                        />
                    )}
                    {tabValue === ManagementTab.GENERAL && <ViewColumnOptions tableType="management-general-table" />}
                </div>
            </div>
        </>
    );
}

export default ManagementGeneralFilter;
